import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import './KeyboardShortcuts.css';

function KeyboardShortcuts() {
  const [showHelp, setShowHelp] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable) {
        return;
      }

      // ? - Show help
      if (e.key === '?' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        setShowHelp(prev => !prev);
        return;
      }

      // Escape - Close help or modals
      if (e.key === 'Escape') {
        setShowHelp(false);
        return;
      }

      // Navigation shortcuts (only when no modifier keys)
      if (!e.ctrlKey && !e.metaKey && !e.altKey) {
        switch (e.key) {
          case 'g':
            // Wait for second key
            const handleSecondKey = (e2: KeyboardEvent) => {
              document.removeEventListener('keydown', handleSecondKey);
              switch (e2.key) {
                case 'd': navigate('/'); break;
                case 'r': navigate('/requests'); break;
                case 'm': navigate('/missions'); break;
                case 'p': navigate('/map'); break;
                case 's': navigate('/statistics'); break;
                case 'n': navigate('/notifications'); break;
              }
            };
            document.addEventListener('keydown', handleSecondKey, { once: true });
            setTimeout(() => document.removeEventListener('keydown', handleSecondKey), 1500);
            break;
          case 'n':
            if (location.pathname === '/requests') {
              navigate('/requests/new');
            }
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [navigate, location.pathname]);

  if (!showHelp) return null;

  return (
    <div className="keyboard-shortcuts-overlay" onClick={() => setShowHelp(false)}>
      <div className="keyboard-shortcuts" onClick={e => e.stopPropagation()}>
        <div className="keyboard-shortcuts-header">
          <h3>Keyboard Shortcuts</h3>
          <button className="keyboard-shortcuts-close" onClick={() => setShowHelp(false)}>
            <X size={18} />
          </button>
        </div>

        <div className="keyboard-shortcuts-body">
          <div className="shortcut-group">
            <h4>Global</h4>
            <div className="shortcut-item">
              <kbd>Ctrl</kbd>+<kbd>K</kbd>
              <span>Search</span>
            </div>
            <div className="shortcut-item">
              <kbd>?</kbd>
              <span>Show shortcuts</span>
            </div>
            <div className="shortcut-item">
              <kbd>Esc</kbd>
              <span>Close modal / dialog</span>
            </div>
          </div>

          <div className="shortcut-group">
            <h4>Navigation (g then...)</h4>
            <div className="shortcut-item">
              <kbd>g</kbd> <kbd>d</kbd>
              <span>Dashboard</span>
            </div>
            <div className="shortcut-item">
              <kbd>g</kbd> <kbd>r</kbd>
              <span>Requests</span>
            </div>
            <div className="shortcut-item">
              <kbd>g</kbd> <kbd>m</kbd>
              <span>Transports</span>
            </div>
            <div className="shortcut-item">
              <kbd>g</kbd> <kbd>p</kbd>
              <span>Map</span>
            </div>
            <div className="shortcut-item">
              <kbd>g</kbd> <kbd>s</kbd>
              <span>Statistics</span>
            </div>
            <div className="shortcut-item">
              <kbd>g</kbd> <kbd>n</kbd>
              <span>Notifications</span>
            </div>
          </div>

          <div className="shortcut-group">
            <h4>Contextual</h4>
            <div className="shortcut-item">
              <kbd>n</kbd>
              <span>New request (on Requests page)</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default KeyboardShortcuts;
